import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n';
import type { TranslationKey } from '../i18n/translations';
import { api, type SubscriptionInfo } from '../api/client';
import LangSwitch from '../components/LangSwitch';
import TokenManager from '../components/TokenManager';
import ChangePassword from '../components/ChangePassword';
import { formatTime } from '../lib/utils';

/** 将 plan 字段映射为翻译键 */
function planKey(plan: string): TranslationKey {
  switch (plan) {
    case 'pro':
      return 'plan.pro';
    case 'enterprise':
      return 'plan.enterprise';
    default:
      return 'plan.free';
  }
}

/**
 * 设置页 — 账户信息 + 订阅管理 + 语言偏好 + 登出
 */
export default function Settings() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser, logout } = useAuth();
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const sections = ['account', 'billing', 'security', 'integrations'] as const;
  const keys: Record<(typeof sections)[number], TranslationKey> = { account: 'workspace.accountNav', billing: 'workspace.billingNav', security: 'workspace.securityNav', integrations: 'workspace.integrationsNav' };
  const section = sections.find((value) => '#' + value === location.hash) ?? 'account';
  const { data: subData, isLoading: subLoading, error: subError } = useQuery({ queryKey: ['billing', 'subscription'], queryFn: () => api.getSubscription(), enabled: !!user });
  const sub = subData?.subscription ?? null;
  const isPaid = user?.plan === 'pro' || user?.plan === 'enterprise';
  const cancelMutation = useMutation({
    mutationFn: () => api.cancelSubscription(sub?.id),
    onSuccess: async () => { await refreshUser(); queryClient.invalidateQueries({ queryKey: ['billing', 'subscription'] }); setShowCancelConfirm(false); },
  });
  if (!user) return null;
  return (
    <div className="workspace-page settings-page">
      <header className="workspace-header"><div><p className="eyebrow">{t('workspace.eyebrow')}</p><h1>{t('settings.title')}</h1><p className="page-subtitle">{t('workspace.settingsSub')}</p></div></header>
      <nav className="settings-nav" aria-label={t('settings.title')}>{sections.map((value) => <Link key={value} to={'#' + value} aria-current={value === section ? 'page' : undefined}>{t(keys[value])}</Link>)}</nav>
      <section className="settings-panel" id={section} aria-label={t(keys[section])}>
        {section === 'account' && <>
          <div className="card"><div className="card-head"><h2 className="card-title">{t('settings.account.title')}</h2><span className="plan-badge">{t(planKey(user.plan))}</span></div><dl className="info-list"><div className="info-row"><dt>{t('settings.account.name')}</dt><dd>{user.name}</dd></div><div className="info-row"><dt>{t('settings.account.email')}</dt><dd>{user.email}</dd></div></dl><details className="guide-disclosure"><summary>{t('workspace.accountDetails')}</summary><p className="form-hint">{t('settings.account.userId')}: <code>{user.id}</code></p></details></div>
          <div className="card"><div className="card-head"><h2 className="card-title">{t('settings.language.title')}</h2></div><LangSwitch full /><p className="form-hint">{t('settings.language.hint')}</p></div>
          <button className="btn btn-ghost" onClick={() => { logout(); navigate('/'); }}>{t('settings.actions.logout')}</button>
        </>}
        {section === 'billing' && <div className="card"><div className="card-head"><h2 className="card-title">{t('settings.plan.title')}</h2></div>
          {subError ? <div className="state-box state-error" role="alert">{(subError as Error).message}</div> : subLoading ? <div className="state-box" role="status">{t('common.loading')}</div> : isPaid && sub ? <SubscriptionDetails sub={sub} lang={lang} showCancelConfirm={showCancelConfirm} setShowCancelConfirm={setShowCancelConfirm} cancelMutation={cancelMutation} t={t} /> : <><p className="page-subtitle">{t(isPaid ? 'settings.plan.benefits' : 'settings.plan.freeIntro')}</p>{!isPaid && <ul className="plan-benefits">{(['settings.plan.b1', 'settings.plan.b2', 'settings.plan.b3', 'settings.plan.b4'] as TranslationKey[]).map((key) => <li key={key}>{t(key)}</li>)}</ul>}<Link className="btn btn-primary" to="/upgrade">{t(isPaid ? 'workspace.viewPlans' : 'settings.plan.upgrade')}</Link></>}
        </div>}
        {section === 'security' && <ChangePassword />}
        {section === 'integrations' && <><p className="page-subtitle">{t('workspace.integrationSub')}</p><TokenManager /></>}
      </section>
    </div>
  );
}

/** 付费订阅详情卡片内容 */
function SubscriptionDetails({
  sub,
  lang,
  showCancelConfirm,
  setShowCancelConfirm,
  cancelMutation,
  t,
}: {
  sub: SubscriptionInfo;
  lang: 'zh' | 'en';
  showCancelConfirm: boolean;
  setShowCancelConfirm: (v: boolean) => void;
  cancelMutation: { isPending: boolean; mutate: () => void; error: Error | null };
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}) {
  const isCanceled = sub.cancelAtPeriodEnd;

  return (
    <div>
      <div className="state-box">
        {t('settings.plan.current')}
        <span className="plan-badge" style={{ marginLeft: '0.5rem' }}>
          {t(planKey(sub.plan))}
        </span>
        {isCanceled && (
          <span style={{ marginLeft: '0.75rem', color: 'var(--warn)', fontSize: '0.82rem' }}>
            {t('billing.cancelAtEnd')}
          </span>
        )}
      </div>

      <dl className="info-list" style={{ marginTop: '0.75rem' }}>
        <div className="info-row">
          <dt>{t('billing.paymentMethod')}</dt>
          <dd style={{ textTransform: 'capitalize' }}>{sub.provider}</dd>
        </div>
        <div className="info-row">
          <dt>{t('billing.amount')}</dt>
          <dd>
            {formatPrice(sub.amount, sub.currency)} / {sub.interval}
          </dd>
        </div>
        <div className="info-row">
          <dt>{t('billing.started')}</dt>
          <dd>{formatTime(sub.startedAt, lang)}</dd>
        </div>
        {sub.currentPeriodEnd && (
          <div className="info-row">
            <dt>{t('billing.renewsOn')}</dt>
            <dd>{formatTime(sub.currentPeriodEnd, lang)}</dd>
          </div>
        )}
        {sub.canceledAt && (
          <div className="info-row">
            <dt>{t('billing.canceledAt')}</dt>
            <dd>{formatTime(sub.canceledAt, lang)}</dd>
          </div>
        )}
      </dl>

      {!isCanceled ? (
        <>
          {!showCancelConfirm ? (
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button
                className="btn btn-ghost"
                style={{ color: 'var(--warn)', borderColor: 'var(--rule)' }}
                onClick={() => setShowCancelConfirm(true)}
              >
                {t('billing.cancelSub')}
              </button>
            </div>
          ) : (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--ink2)', marginBottom: '0.75rem' }}>
                {t('billing.cancelHint')}
              </p>
              {cancelMutation.error && (
                <div className="field-error" style={{ marginBottom: '0.5rem' }}>
                  {cancelMutation.error.message}
                </div>
              )}
              <div className="form-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancelMutation.isPending}
                >
                  {t('billing.keepSub')}
                </button>
                <button
                  className="btn"
                  style={{ background: 'var(--warn)', color: '#fff' }}
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                >
                  {cancelMutation.isPending ? t('billing.canceling') : t('billing.confirmCancel')}
                </button>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

/** 格式化价格：分 → 带货币符号的金额 */
function formatPrice(cents: number, currency: string): string {
  const amount = cents / 100;
  if (currency.toLowerCase() === 'cny') {
    return `¥${amount.toFixed(2)}`;
  }
  return `$${amount.toFixed(2)}`;
}
